@if (loading()) {
  <div class="flex items-center justify-center h-full">
    <mat-spinner diameter="40" />
  </div>
} @else {
  <div class="container flex flex-col mx-auto px-4 md:px-6 pt-8 pb-4">
    <!-- Header -->
    <div class="flex items-center gap-3 mb-6 min-h-10">
      <button class="inline-flex!" mat-icon-button (click)="navigateBack()">
        <mat-icon>arrow_back</mat-icon>
      </button>

      <h2 class="text-2xl font-medium">{{ pageTitle() }}</h2>

      <button
        mat-button
        class="ml-auto"
        [disabled]="form.invalid || saving()"
        (click)="handleSave()"
      >
        <mat-icon>save</mat-icon>
        Opslaan
      </button>
    </div>

    <!-- Two-column layout -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 flex-auto min-h-0">
      <!-- Left column: Role details -->
      <div class="flex flex-col gap-4 col-span-1">
        <div class="bg-white rounded-lg">
          <div class="flex items-center px-6 h-14 border-b border-gray-200">
            <h3 class="text-lg font-semibold text-gray-700">Rol details</h3>
          </div>

          <form [formGroup]="form" class="flex flex-col gap-4 p-6">
            <div class="space-y-1.5">
              <label for="roleName" class="block font-medium text-sm select-none"
                >Naam</label
              >
              <mat-form-field class="w-full">
                <input
                  id="roleName"
                  #roleName
                  matInput
                  formControlName="label"
                  placeholder="e.g. Administrator"
                />
                <mat-hint align="end">{{ roleName.value.length }}/64</mat-hint>

                @if (form.controls.label.hasError('required')) {
                  <mat-error>Vul een naam in</mat-error>
                }
                @if (form.controls.label.hasError('minlength')) {
                  <mat-error>
                    Minimaal
                    {{ form.controls.label.getError('minlength').requiredLength }}
                    karakters
                  </mat-error>
                }
              </mat-form-field>
            </div>

            <div class="space-y-1.5">
              <label
                for="roleDescription"
                class="block font-medium text-sm select-none"
              >
                Omschrijving
                <span class="text-gray-500 font-normal text-xs">(optioneel)</span>
              </label>
              <mat-form-field class="w-full">
                <textarea
                  id="roleDescription"
                  #roleDescription
                  matInput
                  formControlName="description"
                  placeholder="Korte omschrijving van deze rol"
                  rows="3"
                ></textarea>
                <mat-hint align="end">{{ roleDescription.value.length }}/255</mat-hint>
              </mat-form-field>
            </div>
          </form>

          <mat-divider class="mx-6! border-gray-200!" />

          <div class="flex items-center gap-2 p-6 text-sm text-gray-500">
            <mat-icon class="text-base! px-1 -ml-1 text-gray-400">security</mat-icon>
            {{ selectedPermissionCount() }} / {{ totalPermissionCount }} rechten geselecteerd
          </div>
        </div>
      </div>

      <!-- Right column: Permissions -->
      <div class="flex flex-col gap-3 min-h-0 col-span-2">
        <div class="bg-white rounded-lg flex flex-col">
          <div class="flex items-center px-6 h-14 border-b border-gray-200">
            <h3 class="text-lg font-medium text-gray-700">Rechten</h3>
          </div>

          <div class="flex flex-col gap-4 p-6">
            <!-- Search -->
            <div class="flex gap-2">
              <mat-form-field
                subscriptSizing="dynamic"
                class="w-full [--mat-form-field-container-text-size:14px] [--mat-form-field-container-height:48px] [--mat-form-field-container-vertical-padding:12px]"
              >
                <mat-icon matPrefix>search</mat-icon>
                <input
                  #searchInput
                  matInput
                  placeholder="Zoeken op naam of resource..."
                  [value]="permissionSearch()"
                  (input)="handleSearch(searchInput.value)"
                />
                @if (permissionSearch()) {
                  <button
                    mat-icon-button
                    matSuffix
                    (click)="clearSearch(); searchInput.value = ''"
                  >
                    <mat-icon>cancel</mat-icon>
                  </button>
                }
              </mat-form-field>

              <div
                class="inline-flex items-center border border-(--mat-sys-outline) rounded-sm p-1"
              >
                <button
                  class="inline-flex items-center justify-center h-full whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 text-gray-700 hover:bg-gray-100 hover:text-gray-900 hover:cursor-pointer focus-visible:ring-gray-600 rounded-xs px-2 h-8 text-xs font-medium"
                  (click)="expandAll()"
                  type="button"
                >
                  Alles uitklappen
                </button>

                <div class="w-px h-4 bg-(--mat-sys-outline) mx-1"></div>

                <button
                  class="inline-flex items-center justify-center h-full whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 text-gray-700 hover:bg-gray-100 hover:text-gray-900 hover:cursor-pointer focus-visible:ring-gray-600 rounded-xs px-2 h-8 text-xs font-medium"
                  (click)="collapseAll()"
                  type="button"
                >
                  Alles inklappen
                </button>
              </div>
            </div>

            <!-- Action buttons -->
            <!-- <div class="flex flex-wrap items-center gap-2">
              <span class="mx-1 h-4 w-px bg-gray-300"></span>

              <button mat-button (click)="selectAll()" class="!text-xs">
                <mat-icon class="!text-base !size-4">check_box</mat-icon>
                Alles selecteren
              </button>
              <button mat-button (click)="clearAll()" class="!text-xs">
                <mat-icon class="!text-base !size-4">check_box_outline_blank</mat-icon>
                Alles wissen
              </button>
            </div> -->

            <!-- Permission groups -->
            <cdk-accordion class="flex flex-col gap-4" multi>
              @for (group of permissionGroups(); track group.key) {
                <cdk-accordion-item
                  #accordionItem="cdkAccordionItem"
                  [expanded]="isGroupExpanded(group.key)"
                >
                  <div class="border border-gray-300 rounded-lg overflow-hidden">
                    <!-- Group header -->
                    <div
                      class="flex items-center bg-gray-100 px-4 py-2 cursor-pointer select-none"
                      (click)="toggleGroupExpanded(group.key)"
                      role="presentation"
                    >
                      <div
                        class="transition-transform"
                        [class]="{
                          '-rotate-90': !isGroupExpanded(group.key),
                        }"
                      >
                        <mat-icon class="flex-none text-gray-500 text-xl/5! py-1 px-0.5">
                          arrow_drop_down
                        </mat-icon>
                      </div>

                      <span class="text-sm font-medium flex-auto">{{
                        group.label
                      }}</span>

                      @if (getGroupInfo(group.key); as groupInfo) {
                        @if (groupInfo.relatedPermissions.length > 0) {
                          <mat-icon
                            class="text-base/5! size-5! cursor-help px-0.5 transition-colors"
                            [matTooltip]="groupInfo.description"
                            matTooltipPosition="above"
                            (click)="$event.stopPropagation()"
                          >
                            info
                          </mat-icon>
                        }
                      }

                      <mat-checkbox
                        [checked]="isGroupAllSelected(group)"
                        [indeterminate]="isGroupIndeterminate(group)"
                        (change)="toggleGroup(group)"
                        (click)="$event.stopPropagation()"
                      >
                        Alles
                      </mat-checkbox>
                    </div>

                    <!-- Group content (permissions) -->
                    <div
                      [@expandCollapse]="isGroupExpanded(group.key) ? 'expanded' : 'collapsed'"
                    >
                      <div
                        class="flex flex-col gap-0 py-3 px-4 border-t border-gray-300"
                      >
                        <div class="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-1">
                          @for (permission of group.permissions; track permission.key) {
                            <mat-checkbox
                              [checked]="isPermissionSelected(permission.value)"
                              (change)="togglePermission(permission.value)"
                            >
                              {{ permission.actionLabel }}
                            </mat-checkbox>
                          }
                        </div>

                        <!-- Missing :read prerequisite warning -->
                        @if (getMissingReadHint(group); as readHint) {
                          <div
                            class="flex items-start gap-1 mt-2 p-2 rounded bg-amber-50 border border-amber-300 text-amber-900"
                          >
                            <mat-icon
                              class="text-base/5! size-5! px-0.5 shrink-0 mt-px text-amber-600"
                            >
                              warning
                            </mat-icon>
                            <span class="text-xs my-auto flex-auto">{{ readHint.message }}</span>
                            <button
                              type="button"
                              class="shrink-0 my-auto ml-2 text-xs font-medium underline underline-offset-2 hover:no-underline cursor-pointer"
                              (click)="addPermissions([readHint.permission])"
                            >
                              Toevoegen
                            </button>
                          </div>
                        }

                        <!-- What the selected permissions need from other groups:
                             amber when they do not work without, blue when part
                             of their pages stays hidden -->
                        @for (notice of getDependencyNotices(group); track notice.message) {
                          <div
                            class="flex items-start gap-1 mt-2 p-2 rounded border"
                            [class]="
                              notice.strength === 'required'
                                ? 'bg-amber-50 border-amber-300 text-amber-900'
                                : 'bg-blue-50 border-blue-200 text-blue-800'
                            "
                          >
                            <mat-icon
                              class="text-base/5! size-5! px-0.5 shrink-0 mt-px"
                              [class]="
                                notice.strength === 'required' ? 'text-amber-600' : 'text-blue-600'
                              "
                            >
                              {{ notice.strength === 'required' ? 'warning' : 'lightbulb' }}
                            </mat-icon>
                            <span class="text-xs my-auto flex-auto">{{ notice.message }}</span>
                            <button
                              type="button"
                              class="shrink-0 my-auto ml-2 text-xs font-medium underline underline-offset-2 hover:no-underline cursor-pointer"
                              (click)="addPermissions(notice.missing)"
                            >
                              Toevoegen
                            </button>
                          </div>
                        }
                      </div>
                    </div>
                  </div>
                </cdk-accordion-item>
              } @empty {
                <div class="flex flex-col items-center justify-center py-20 text-center">

                  <div class="size-20 flex items-center justify-center rounded-full mx-auto bg-secondary-100 text-secondary-700">
                    
                    <mat-icon>search</mat-icon>
                  
                  </div>

                  <div class="text-sm text-gray-500 py-4 text-center">
                    Geen rechten gevonden voor "{{ permissionSearch() }}"
                  </div>

                </div>
              }
            </cdk-accordion>
          </div>
        </div>
      </div>
    </div>
  </div>
}
